import {
  useFileSignedUrlFetcher,
  useRolodexFileLS,
  useRolodexSignedURLs,
} from 'features/rolodex'
import { ComponentId } from 'pages/reporting/reports/rug/types'
import {
  type ParsedReportHeatmapAssetPath,
  parseReportHeatmapAssetPath,
} from 'pages/reporting/reports/rug/utils/reportHeatmapStorage'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface ReportHeatmapAsset {
  componentId: ComponentId
  sectionKey?: string
  descriptorHash: string
  bundlePath: string
  heatmapPath: string
  heatmapSignedUrl?: string
  legendPath?: string
  legendSignedUrl?: string
  descriptorPath?: string
  imagePath: string
  signedUrl?: string
}

export interface AssetScreenshots {
  fullComponentHeatmap?: ReportHeatmapAsset[]
  sectionHeatmap?: ReportHeatmapAsset[]
  fullComponentDrawing?: {
    componentId: ComponentId
    imagePath: string
    signedUrl?: string
    extension: string
  }[]
}

interface AssetDrawingUrlResult {
  path?: string
  signed_url?: string
}

export interface RegisterGeneratedReportHeatmapBundleInput {
  heatmapPath: string
  heatmapBlob?: Blob
  legendPath: string
  legendBlob?: Blob
  descriptorPath: string
}

function revokeObjectUrl(url: string | undefined) {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

function revokeHeatmapAssetObjectUrls(asset: ReportHeatmapAsset) {
  revokeObjectUrl(asset.signedUrl)
  revokeObjectUrl(asset.heatmapSignedUrl)
  revokeObjectUrl(asset.legendSignedUrl)
}

function createEmptyReportHeatmapAsset(
  parsedPath: ParsedReportHeatmapAssetPath
): ReportHeatmapAsset {
  return {
    componentId: parsedPath.componentId,
    sectionKey: parsedPath.sectionKey,
    descriptorHash: parsedPath.descriptorHash,
    bundlePath: parsedPath.bundlePath,
    heatmapPath: '',
    imagePath: '',
    signedUrl: undefined,
  }
}

function getReportHeatmapAssetPatch(
  parsedPath: ParsedReportHeatmapAssetPath,
  result: AssetDrawingUrlResult
): Partial<ReportHeatmapAsset> {
  switch (parsedPath.fileName) {
    case 'heatmap.png':
      return {
        heatmapPath: result.path ?? '',
        heatmapSignedUrl: result.signed_url,
        imagePath: result.path ?? '',
        signedUrl: result.signed_url,
      }
    case 'legend.png':
      return {
        legendPath: result.path,
        legendSignedUrl: result.signed_url,
      }
    case 'descriptor.json':
      return {
        descriptorPath: result.path,
      }
  }
}

export const useAssetScreenshots = (metadataEntryId: string) => {
  const [optimisticHeatmapAssets, setOptimisticHeatmapAssets] = useState<
    ReportHeatmapAsset[]
  >([])
  const optimisticHeatmapAssetsRef = useRef(optimisticHeatmapAssets)
  const { fetchSignedUrl } = useFileSignedUrlFetcher()

  useEffect(() => {
    optimisticHeatmapAssetsRef.current = optimisticHeatmapAssets
  }, [optimisticHeatmapAssets])

  useEffect(() => {
    return () => {
      optimisticHeatmapAssetsRef.current.forEach(revokeHeatmapAssetObjectUrls)
    }
  }, [])

  const { fileRefs: screenshotRefs, forceRefresh: forceRefreshScreenshotRefs } =
    useRolodexFileLS({
      entry_id: metadataEntryId,
      path: 'asset-screenshot/**',
    })

  const { fileRefs: drawingRefs, forceRefresh: forceRefreshDrawingRefs } =
    useRolodexFileLS({
      entry_id: metadataEntryId,
      path: 'asset-drawing/**',
    })

  const allFileRefs = useMemo(
    () => [...screenshotRefs, ...drawingRefs],
    [screenshotRefs, drawingRefs]
  )

  const {
    results: assetDrawingUrls,
    forceRefresh: forceRefreshAssetDrawingUrls,
  } = useRolodexSignedURLs(
    allFileRefs.map((ref) => ({
      entry_id: metadataEntryId,
      path: ref.path,
    }))
  )

  const assetDrawings = useMemo(() => {
    const drawings: AssetScreenshots = {
      fullComponentHeatmap: [],
      sectionHeatmap: [],
      fullComponentDrawing: [],
    }
    const reportHeatmapBundles = new Map<string, ReportHeatmapAsset>()

    assetDrawingUrls.forEach((result) => {
      const parsedPath = parseReportHeatmapAssetPath(result.path)

      if (parsedPath) {
        const existingBundle =
          reportHeatmapBundles.get(parsedPath.bundlePath) ??
          createEmptyReportHeatmapAsset(parsedPath)

        reportHeatmapBundles.set(parsedPath.bundlePath, {
          ...existingBundle,
          ...getReportHeatmapAssetPatch(parsedPath, result),
        })
      } else if (result.path?.includes('fullComponentDrawing')) {
        // Extract component ID and extension from path: asset-drawing/fullComponentDrawing/<componentId>.<extension>
        const match = result.path.match(/fullComponentDrawing\/(.+)\.(\w+)$/)
        if (match) {
          const componentId = match[1] as ComponentId
          const extension = match[2]
          drawings.fullComponentDrawing?.push({
            componentId,
            imagePath: result.path,
            signedUrl: result.signed_url,
            extension,
          })
        }
      }
    })

    reportHeatmapBundles.forEach((bundle) => {
      if (!bundle.heatmapPath) return

      if (bundle.sectionKey) {
        drawings.sectionHeatmap?.push(bundle)
      } else {
        drawings.fullComponentHeatmap?.push(bundle)
      }
    })

    optimisticHeatmapAssets.forEach((asset) => {
      if (asset.sectionKey) {
        const existingIndex = drawings.sectionHeatmap?.findIndex(
          (candidate) =>
            candidate.componentId === asset.componentId &&
            candidate.sectionKey === asset.sectionKey
        )

        if (existingIndex === undefined || existingIndex < 0) {
          drawings.sectionHeatmap?.push(asset)
        } else {
          drawings.sectionHeatmap?.splice(existingIndex, 1, asset)
        }
        return
      }

      const existingIndex = drawings.fullComponentHeatmap?.findIndex(
        (candidate) => candidate.componentId === asset.componentId
      )

      if (existingIndex === undefined || existingIndex < 0) {
        drawings.fullComponentHeatmap?.push(asset)
      } else {
        drawings.fullComponentHeatmap?.splice(existingIndex, 1, asset)
      }
    })

    return drawings
  }, [assetDrawingUrls, optimisticHeatmapAssets])

  const forceRefresh = useCallback(async () => {
    await forceRefreshScreenshotRefs()
    await forceRefreshDrawingRefs()
    await forceRefreshAssetDrawingUrls()
  }, [
    forceRefreshScreenshotRefs,
    forceRefreshDrawingRefs,
    forceRefreshAssetDrawingUrls,
  ])

  const registerGeneratedHeatmapBundle = useCallback(
    async ({
      heatmapPath,
      heatmapBlob,
      legendPath,
      legendBlob,
      descriptorPath,
    }: RegisterGeneratedReportHeatmapBundleInput) => {
      const heatmapParsedPath = parseReportHeatmapAssetPath(heatmapPath)
      if (!heatmapParsedPath) {
        throw new Error(`Unable to parse generated heatmap path ${heatmapPath}`)
      }

      const signedUrlResults = await fetchSignedUrl({
        queryOrQueries: [heatmapPath, legendPath, descriptorPath].map(
          (path) => ({
            entry_id: metadataEntryId,
            path,
          })
        ),
      }).catch(() => undefined)

      const nextAsset = (
        signedUrlResults?.results ?? []
      ).reduce<ReportHeatmapAsset>((asset, result) => {
        const parsedPath = parseReportHeatmapAssetPath(result.path)
        if (!parsedPath || parsedPath.bundlePath !== asset.bundlePath) {
          return asset
        }

        return {
          ...asset,
          ...getReportHeatmapAssetPatch(parsedPath, result),
        }
      }, createEmptyReportHeatmapAsset(heatmapParsedPath))

      if (!nextAsset.signedUrl && heatmapBlob) {
        const heatmapObjectUrl = URL.createObjectURL(heatmapBlob)
        nextAsset.heatmapPath = heatmapPath
        nextAsset.imagePath = heatmapPath
        nextAsset.heatmapSignedUrl = heatmapObjectUrl
        nextAsset.signedUrl = heatmapObjectUrl
      }

      if (!nextAsset.legendSignedUrl && legendBlob) {
        nextAsset.legendPath = legendPath
        nextAsset.legendSignedUrl = URL.createObjectURL(legendBlob)
      }

      if (!nextAsset.descriptorPath) {
        nextAsset.descriptorPath = descriptorPath
      }

      if (!nextAsset.signedUrl) {
        throw new Error(
          `Unable to create display URL for generated heatmap ${heatmapPath}`
        )
      }

      setOptimisticHeatmapAssets((currentAssets) => {
        const replacedAssets = currentAssets.filter(
          (asset) =>
            asset.componentId === nextAsset.componentId &&
            asset.sectionKey === nextAsset.sectionKey
        )
        replacedAssets.forEach(revokeHeatmapAssetObjectUrls)

        return [
          ...currentAssets.filter(
            (asset) =>
              asset.componentId !== nextAsset.componentId ||
              asset.sectionKey !== nextAsset.sectionKey
          ),
          nextAsset,
        ]
      })
    },
    [fetchSignedUrl, metadataEntryId]
  )

  return {
    assetScreenshots: assetDrawings,
    forceRefresh,
    registerGeneratedHeatmapBundle,
  }
}
